import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, RotateCcw, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";

function Question({ q, i, value, onPick, res }) {
  return (
    <fieldset className="border border-zinc-200 p-4" data-testid={`quiz-q-${q.id}`}>
      <legend className="px-1 font-bold text-sm"><span className="text-[#dc2626] mr-1">{i + 1}.</span>{q.text}</legend>
      <div className="grid gap-2 mt-2">
        {q.options.map((o, oi) => {
          const tone = !res ? (value === oi ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400")
            : oi === res.correct_index ? "border-green-600 bg-green-50" : oi === value ? "border-[#dc2626] bg-red-50" : "border-zinc-200 opacity-70";
          return (
            <label key={oi} className={`flex gap-2 items-start text-sm p-2.5 border-2 cursor-pointer transition-colors ${tone}`}>
              <input type="radio" name={q.id} className="accent-[#dc2626] mt-0.5" disabled={!!res} checked={value === oi} onChange={() => onPick(oi)} data-testid={`quiz-opt-${q.id}-${oi}`} />{o}
            </label>
          );
        })}
      </div>
      {res && <p className={`mt-3 text-sm flex gap-2 ${res.is_correct ? "text-green-700" : "text-[#dc2626]"}`} data-testid={`quiz-feedback-${q.id}`}>
        {res.is_correct ? <CheckCircle2 size={16} className="shrink-0" /> : <XCircle size={16} className="shrink-0" />}
        <span><b>{res.is_correct ? "Doğru." : value === undefined ? "Boş bırakıldı." : "Yanlış."}</b> {res.explanation}</span></p>}
    </fieldset>
  );
}

export default function LessonQuiz({ slug, user, best, onDone }) {
  const [qs, setQs] = useState(null);
  const [ans, setAns] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true); setResult(null); setAns({});
    try { setQs((await api.get(`/lessons/${slug}/quiz`)).data.questions); } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    const answers = Object.fromEntries(qs.map((q) => [q.id, ans[q.id] ?? -1]));
    try { const r = (await api.post(`/lessons/${slug}/quiz`, { answers })).data; setResult(r); onDone?.(r.percent); window.scrollTo({ top: document.getElementById("ders-testi").offsetTop - 90, behavior: "smooth" }); }
    catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  const byId = Object.fromEntries((result?.details || []).map((d) => [d.id, d]));
  return (
    <section id="ders-testi" className="card-ada p-5 sm:p-6 scroll-mt-24 border-t-4 !border-t-[#dc2626]" data-testid="lesson-quiz">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div><h2 className="font-black text-xl flex items-center gap-2"><ClipboardCheck className="text-[#dc2626]" size={22} />Ders Sonu Testi</h2>
          <p className="text-sm text-zinc-600 mt-1">Okuduklarını hemen pekiştir. Her açılışta soru havuzundan rastgele sorular gelir.</p>
          {user ? best != null && <p className="text-xs mt-1 font-bold" data-testid="quiz-best">En iyi sonucun: %{best}</p>
            : <p className="text-xs mt-1 text-zinc-500">Sonuçlarının kaydedilmesi için <Link to="/kursiyer-girisi" className="underline">kursiyer girişi</Link> yap.</p>}
        </div>
        {!qs && <button className="btn-red" onClick={start} disabled={busy} data-testid="quiz-start-btn">{busy && <Loader2 size={16} className="animate-spin" />}Teste Başla</button>}
      </div>
      {result && (
        <div className="mt-5 bg-zinc-950 text-white p-5 flex flex-wrap items-center justify-between gap-3" data-testid="quiz-result">
          <div><p className="text-xs uppercase tracking-widest text-zinc-400">Sonuç</p><p className="text-3xl font-black"><span className={result.percent >= 70 ? "text-green-400" : "text-[#ef4444]"}>%{result.percent}</span> <span className="text-base font-semibold text-zinc-300">{result.correct}/{result.total} doğru</span></p>
            <p className="text-sm text-zinc-300">{result.percent >= 70 ? "Tebrikler, bu dersi iyi kavramışsın." : "Notları tekrar gözden geçirip yeniden dene."}</p></div>
          <button className="btn-red" onClick={start} disabled={busy} data-testid="quiz-retry-btn"><RotateCcw size={16} />Yeni Sorularla Tekrar Dene</button>
        </div>
      )}
      {qs && (qs.length ? (
        <div className="grid gap-4 mt-5">
          {qs.map((q, i) => <Question key={q.id} q={q} i={i} value={ans[q.id]} res={byId[q.id]} onPick={(v) => setAns({ ...ans, [q.id]: v })} />)}
          {!result && <div className="flex flex-wrap items-center gap-3"><button className="btn-red" onClick={submit} disabled={busy} data-testid="quiz-submit-btn">{busy && <Loader2 size={16} className="animate-spin" />}Testi Bitir</button>
            <span className="text-xs text-zinc-500" data-testid="quiz-answered">{Object.keys(ans).length}/{qs.length} soru yanıtlandı</span></div>}
        </div>
      ) : <p className="mt-4 text-sm text-zinc-500" data-testid="quiz-empty">Bu ders için henüz soru eklenmedi.</p>)}
    </section>
  );
}
